import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { useT } from '../../i18n'
import { problemReportingRolloutI18n } from '../../i18n/settings/problem-reporting-rollout'
import { apiUrl } from '../../lib/api'

const CONFIG_KEY = 'problem_reporting.enabled'
const QUERY_KEY = ['admin', 'config', CONFIG_KEY] as const

class ConfigError extends Error {
  readonly status?: number

  constructor(status?: number) {
    super('Problem reporting config is unavailable')
    this.status = status
  }
}

function parseConfig(payload: unknown): boolean {
  if (
    typeof payload !== 'object' || payload === null || Array.isArray(payload) ||
    !('config_key' in payload) || payload.config_key !== CONFIG_KEY ||
    !('value_type' in payload) || payload.value_type !== 'boolean' ||
    !('config_value' in payload) || typeof payload.config_value !== 'object' ||
    payload.config_value === null || Array.isArray(payload.config_value) ||
    !('value' in payload.config_value) || typeof payload.config_value.value !== 'boolean'
  ) throw new ConfigError()
  return payload.config_value.value
}

async function readEnabled(allowUnset: boolean): Promise<boolean> {
  const response = await fetch(apiUrl(`/admin/config/${CONFIG_KEY}`))
  if (allowUnset && response.status === 404) {
    try {
      const body: unknown = await response.json()
      if (
        typeof body === 'object' && body !== null && 'detail' in body &&
        typeof body.detail === 'object' && body.detail !== null &&
        'code' in body.detail && body.detail.code === 'admin_config_key_unset'
      ) return false
    } catch {
      // A proxy/route 404 is not evidence of an unset config row.
    }
    throw new ConfigError(404)
  }
  if (!response.ok) throw new ConfigError(response.status)
  try {
    return parseConfig(await response.json())
  } catch (error) {
    if (error instanceof ConfigError) throw error
    throw new ConfigError(response.status)
  }
}

async function writeAndVerify(next: boolean): Promise<boolean> {
  const response = await fetch(apiUrl(`/admin/config/${CONFIG_KEY}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ config_value: { value: next }, value_type: 'boolean' }),
  })
  if (!response.ok) throw new ConfigError(response.status)
  const acknowledged = parseConfig(await response.json())
  if (acknowledged !== next || (await readEnabled(false)) !== next) throw new ConfigError()
  return next
}

export function ProblemReportingRolloutControl() {
  const t = useT(problemReportingRolloutI18n)
  const queryClient = useQueryClient()
  const [reviewOpen, setReviewOpen] = useState(false)
  const setting = useQuery<boolean, ConfigError>({
    queryKey: QUERY_KEY,
    queryFn: () => readEnabled(true),
    retry: false,
  })
  const enabled = setting.data
  const mutation = useMutation<boolean, ConfigError, boolean>({
    mutationFn: writeAndVerify,
    onSuccess: (confirmed) => {
      queryClient.setQueryData(QUERY_KEY, confirmed)
      setReviewOpen(false)
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  return (
    <section className="max-w-3xl space-y-5" aria-labelledby="problem-reporting-title">
      <header>
        <h2 id="problem-reporting-title" className="text-lg font-semibold text-[var(--color-text)]">
          {t.title}
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-5 text-[var(--color-text-muted)]">
          {t.description}
        </p>
      </header>

      {setting.isPending ? (
        <p role="status" className="text-sm text-[var(--color-text-muted)]">{t.loading}</p>
      ) : setting.isError ? (
        <div role="alert" className="rounded-[var(--radius-panel)] border border-[var(--color-border)] p-4">
          <p className="text-sm text-[var(--color-text)]">
            {setting.error.status === 403 ? t.permissionDenied : t.loadError}
          </p>
          {setting.error.status !== 403 ? (
            <Button type="button" variant="outline" className="mt-3" onClick={() => void setting.refetch()}>
              {t.retry}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t.title}: <strong className="text-[var(--color-text)]">{enabled ? t.on : t.off}</strong>
          </p>
          <Button
            type="button"
            className="mt-4"
            disabled={setting.isFetching || mutation.isPending}
            onClick={() => { mutation.reset(); setReviewOpen(true) }}
          >
            {enabled ? t.disable : t.enable}
          </Button>
        </div>
      )}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.reviewTitle}</DialogTitle>
            <DialogDescription>{enabled ? t.disableImpact : t.enableImpact}</DialogDescription>
          </DialogHeader>
          {mutation.isError ? <p role="alert" className="text-sm text-[var(--color-danger)]">{t.saveError}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={mutation.isPending} onClick={() => setReviewOpen(false)}>
              {t.cancel}
            </Button>
            <Button
              type="button"
              disabled={enabled === undefined || setting.isFetching || mutation.isPending || mutation.isError}
              onClick={() => { if (enabled !== undefined) mutation.mutate(!enabled) }}
            >
              {mutation.isPending ? t.saving : enabled ? t.confirmDisable : t.confirmEnable}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
